import type { ISerialize } from '@/objects/ISerialize';
import { ImportAdditionalValues } from './ImportAdditionalValues';
import type { ImportDataFormat } from './../enums/ImportDataFormat';
import type { ImportDataProvider } from './../enums/ImportDataProvider';
import type { ImportDataType } from './../enums/ImportDataType';

export class ImportFormat implements ISerialize {
    public AdditionalValues: ImportAdditionalValues | null;

    public Charset: string;

    public DataFormat: ImportDataFormat;

    public DataProvider: ImportDataProvider;

    public DataType: ImportDataType;

    public FieldDelimiter: string | null;

    public LineDelimiter: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AdditionalValues?: ImportAdditionalValues | null;
        Charset?: string | null;
        DataFormat?: ImportDataFormat;
        DataProvider?: ImportDataProvider;
        DataType?: ImportDataType;
        FieldDelimiter?: string | null;
        LineDelimiter?: string | null;
    }) {
        this.AdditionalValues = values.AdditionalValues ?? null;

        this.Charset = values.Charset ?? '';

        this.DataFormat = values.DataFormat ?? 'Excel';

        this.DataProvider = values.DataProvider ?? 'Generic';

        this.DataType = values.DataType ?? 'All';

        this.FieldDelimiter = values.FieldDelimiter ?? null;

        this.LineDelimiter = values.LineDelimiter ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"AdditionalValues": ${!this.AdditionalValues ? null : this.AdditionalValues?.serialize()}` +
            ',' +
            `"Charset": ${JSON.stringify(this.Charset)}` +
            ',' +
            `"DataFormat": ${this.DataFormat == 'Excel' ? 0 : this.DataFormat == 'Csv' ? 1 : this.DataFormat == 'Custom' ? 2 : this.DataFormat == 'Unknown' ? 3 : this.DataFormat == 'XML' ? 4 : this.DataFormat == 'JSON' ? 5 : 0}` +
            ',' +
            `"DataProvider": ${this.DataProvider == 'Generic' ? 0 : this.DataProvider == 'EQTiming' ? 1 : this.DataProvider == 'FIS' ? 2 : this.DataProvider == 'NIF' ? 3 : 0}` +
            ',' +
            `"DataType": ${this.DataType == 'All' ? 0 : this.DataType == 'Contestants' ? 1 : this.DataType == 'Results' ? 2 : 0}` +
            ',' +
            `"FieldDelimiter": ${JSON.stringify(this.FieldDelimiter)}` +
            ',' +
            `"LineDelimiter": ${JSON.stringify(this.LineDelimiter)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ImportFormat from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AdditionalValues?: ImportAdditionalValues | null;
        Charset?: string | null;
        DataFormat?: ImportDataFormat;
        DataProvider?: ImportDataProvider;
        DataType?: ImportDataType;
        FieldDelimiter?: string | null;
        LineDelimiter?: string | null;
    }): ImportFormat {
        return new ImportFormat(values);
    }

    public static _FromServerJson(json: any): ImportFormat {
        return new ImportFormat({
            AdditionalValues: json.AdditionalValues ? ImportAdditionalValues._FromServerJson(json.AdditionalValues) : null,
            Charset: json.Charset ?? '',
            DataFormat:
                json.DataFormat == 0
                    ? 'Excel'
                    : json.DataFormat == 1
                      ? 'Csv'
                      : json.DataFormat == 2
                        ? 'Custom'
                        : json.DataFormat == 3
                          ? 'Unknown'
                          : json.DataFormat == 4
                            ? 'XML'
                            : json.DataFormat == 5
                              ? 'JSON'
                              : 'Excel',
            DataProvider:
                json.DataProvider == 0
                    ? 'Generic'
                    : json.DataProvider == 1
                      ? 'EQTiming'
                      : json.DataProvider == 2
                        ? 'FIS'
                        : json.DataProvider == 3
                          ? 'NIF'
                          : 'Generic',
            DataType: json.DataType == 0 ? 'All' : json.DataType == 1 ? 'Contestants' : json.DataType == 2 ? 'Results' : 'All',
            FieldDelimiter: json.FieldDelimiter ?? '',
            LineDelimiter: json.LineDelimiter ?? '',
        });
    }

    /**
     * Creates an instance of ImportFormat from another instance of ImportFormat.
     * @param other The ImportFormat you want to clone
     * @returns A cloned ImportFormat
     */
    public static FromOther(other: ImportFormat): ImportFormat {
        return ImportFormat._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ImportFormat.FromOther(this);
    }

    static NAME_OF_CLASS = 'ImportFormat';
}
